import React from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import type { Session } from '../../api/sessionsService';

dayjs.extend(utc);
dayjs.extend(timezone);

export type HoveredDataBar =
  | { type: string; start: Date; end: Date }
  | { type: string; issuanceTime: Date };

export interface SessionTooltipProps {
  session: Session;
  position: { x: number; y: number };
  theme: 'light' | 'dark';
  timezone: string;
}

export const SessionTooltip: React.FC<SessionTooltipProps> = ({
  session,
  position,
  theme,
  timezone: tz,
}) => {
  const formatCalendarDate = (dateStr: string, includeDate: boolean) => {
    const dateInEventTZ = dayjs.utc(dateStr).tz(tz);
    return includeDate ? dateInEventTZ.format('MMM D, HH:mm') : dateInEventTZ.format('HH:mm');
  };

  return (
    <div
      style={{
        position: 'fixed',
        left: position.x,
        top: position.y,
        transform: 'translateX(-50%)',
        backgroundColor: theme === 'dark' ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.95)',
        color: theme === 'dark' ? '#fff' : '#000',
        padding: '8px 12px',
        borderRadius: '6px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
        border: `1px solid ${theme === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.15)'}`,
        pointerEvents: 'none',
        zIndex: 1000,
        fontSize: '12px',
        whiteSpace: 'nowrap',
        maxWidth: '300px',
      }}
    >
      <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{session.session_name}</div>
      <div style={{ opacity: 0.8, marginBottom: session.description ? '4px' : '0' }}>
        {formatCalendarDate(session.start_time, true)} - {formatCalendarDate(session.end_time, false)}
      </div>
      {session.description && (
        <div
          style={{
            opacity: 0.7,
            fontSize: '11px',
            whiteSpace: 'normal',
            lineHeight: '1.4',
          }}
        >
          {session.description}
        </div>
      )}
    </div>
  );
};

export interface DataBarTooltipProps {
  dataBar: HoveredDataBar;
  position: { x: number; y: number };
  theme: 'light' | 'dark';
  timezone: string;
}

export const DataBarTooltip: React.FC<DataBarTooltipProps> = ({
  dataBar,
  position,
  theme,
  timezone: tz,
}) => {
  const formatCalendarDate = (date: Date) => dayjs(date).tz(tz).format('MMM D, HH:mm');
  const isForecastIssuance = 'issuanceTime' in dataBar;

  return (
    <div
      style={{
        position: 'fixed',
        left: position.x,
        top: position.y,
        transform: 'translateX(-50%)',
        backgroundColor: theme === 'dark' ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.95)',
        color: theme === 'dark' ? '#fff' : '#000',
        padding: '8px 12px',
        borderRadius: '6px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
        border: `1px solid ${theme === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.15)'}`,
        pointerEvents: 'none',
        zIndex: 1000,
        fontSize: '12px',
        whiteSpace: 'nowrap',
      }}
    >
      <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{dataBar.type}</div>
      {isForecastIssuance ? (
        <div style={{ opacity: 0.8 }}>{formatCalendarDate(dataBar.issuanceTime)}</div>
      ) : (
        <div style={{ opacity: 0.8 }}>
          <div style={{ marginBottom: '2px' }}>
            <span style={{ opacity: 0.7 }}>Start: </span>
            {formatCalendarDate(dataBar.start)}
          </div>
          <div>
            <span style={{ opacity: 0.7 }}>End: </span>
            {formatCalendarDate(dataBar.end)}
          </div>
        </div>
      )}
    </div>
  );
};
